import * as THREE from "three";
import { MACHINE_PRESETS } from "../components/Sidebar";

// ✅ Return currently selected preset for a machine type
export function getSelectedPreset(machineType) {
  const selected =
    window.labConstraints?.machineSettings?.[machineType]?.selected;
  const preset = selected && MACHINE_PRESETS[machineType]?.[selected];

  if (preset) return { preset, selected };

  // fallback defaults
  if (machineType === "printer3D") {
    return { preset: MACHINE_PRESETS.printer3D["Prusa MK3S+"], selected: "Prusa MK3S+" };
  }
  if (machineType === "laserCutter") {
    return { preset: MACHINE_PRESETS.laserCutter["Trotec Speedy 400"], selected: "Trotec Speedy 400" };
  }
  if (machineType === "wireBender") {
    return { preset: MACHINE_PRESETS.wireBender["Manual Wire Bender"], selected: "Manual Wire Bender" };
  }
  return { preset: null, selected: "Default" };
}

// ✅ Compute object size (bounding box)
export function getObjectSize(object3D) {
  const box = new THREE.Box3().setFromObject(object3D);
  const sz = new THREE.Vector3();
  box.getSize(sz);
  return { width: sz.x, height: sz.y, depth: sz.z };
}

// ✅ Build a warning object (uniform format for <ConstraintWarning>)
export function buildWarning(part, printer, currentSize, maxSize) {
  return {
    type: "size_exceeded",
    part,
    printer,
    currentSize: {
      width: Number(currentSize.width.toFixed(1)),
      height: Number(currentSize.height.toFixed(1)),
      depth: Number(currentSize.depth.toFixed(1)),
    },
    maxSize,
  };
}

// === Specific checks ===

// 3D printer check
export function checkPrinterFit(object3D) {
  const { preset, selected } = getSelectedPreset("printer3D");
  if (!preset) return null;

  const sz = getObjectSize(object3D);
  const tooBig =
    sz.width > preset.width || sz.height > preset.height || sz.depth > preset.depth;

  return tooBig ? buildWarning("Imported Model", selected, sz, preset) : null;
}

// Laser cutter check
export function checkLaserFit(object3D) {
  const { preset, selected } = getSelectedPreset("laserCutter");
  if (!preset) return null;

  const sz = getObjectSize(object3D);
  const modelMax = Math.max(sz.width, sz.height);
  const bedMin = Math.min(preset.width, preset.height);

  return modelMax > bedMin
    ? buildWarning("Sheet Layout", selected, { width: modelMax, height: modelMax, depth: 0 }, { width: bedMin, height: bedMin, depth: 0 })
    : null;
}

// Wire bender check
export function checkWireFit(length, gauge) {
  const { preset, selected } = getSelectedPreset("wireBender");
  if (!preset) return null;

  const tooLong = length > preset.maxLength;
  const tooThick = gauge > preset.maxWireGauge;

  if (tooLong || tooThick) {
    return {
      type: "wire_exceeded",
      part: "Wire Job",
      printer: selected,
      currentSize: { width: length, height: gauge, depth: 0 },
      maxSize: { width: preset.maxLength, height: preset.maxWireGauge, depth: 0 },
    };
  }
  return null;
}

// ✅ Sidebar event listener (Sidebar dispatches "labConstraints:updated")
export function onLabConstraintsChange(handler) {
  const evt = "labConstraints:updated";
  const listener = (e) => handler(e.detail ?? window.labConstraints);
  window.addEventListener(evt, listener);
  return () => window.removeEventListener(evt, listener);
}
